var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
var __rest = this && this.__rest || function (s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
};
import React, { createContext, useEffect, useMemo, useState } from 'react';
import { differenceInMonths, format, parseISO, startOfMonth } from 'date-fns';
import { defaultToday, defaultMaxDate, DEFAULT_MAX_MONTH_INDEX } from '../constants/dates';
import useCalendarForm from '../useCalendarForm';
import buildMonths from '../utils/buildMonths';
export var CalendarContext = createContext(null);
function CalendarProvider(_a) {
  var _b = _a.boxShadow,
    boxShadow = _b === void 0 ? true : _b,
    children = _a.children,
    _c = _a.dayWithRoundedCorners,
    dayWithRoundedCorners = _c === void 0 ? true : _c,
    _d = _a.displayYear,
    displayYear = _d === void 0 ? true : _d,
    locale = _a.locale,
    _e = _a.maxDate,
    maxDate = _e === void 0 ? defaultMaxDate : _e,
    _f = _a.minDate,
    minDate = _f === void 0 ? defaultToday : _f,
    _g = _a.monthsToShow,
    monthsToShow = _g === void 0 ? 2 : _g,
    _h = _a.monthWidth,
    monthWidth = _h === void 0 ? 350 : _h,
    scrollDate = _a.scrollDate,
    startDate = _a.startDate,
    useDefaultFooter = _a.useDefaultFooter,
    onDismiss = _a.onDismiss,
    _j = _a.onDisplayMonthsChange,
    onDisplayMonthsChange = _j === void 0 ? function () {} : _j,
    props = __rest(_a, ["boxShadow", "children", "dayWithRoundedCorners", "displayYear", "locale", "maxDate", "minDate", "monthsToShow", "monthWidth", "scrollDate", "startDate", "useDefaultFooter", "onDismiss", "onDisplayMonthsChange"]);
  var _k = useCalendarForm(__assign(__assign({}, props), {
      maxDate: maxDate,
      minDate: minDate,
      scrollDate: scrollDate,
      startDate: startDate,
      onDismiss: onDismiss
    })),
    handleKeyDismiss = _k.handleKeyDismiss,
    calendarFormResponse = __rest(_k, ["handleKeyDismiss"]);
  useEffect(function () {
    document.addEventListener('keydown', handleKeyDismiss);
    return function () {
      document.removeEventListener('keydown', handleKeyDismiss);
    };
  }, [handleKeyDismiss]);
  var scrollMonth = scrollDate || startDate;
  var scrollToMonth = scrollMonth ? differenceInMonths(parseISO(scrollMonth.toString()), startOfMonth(parseISO(minDate.toString()))) : 0;
  var _l = useState(scrollToMonth),
    monthIndex = _l[0],
    setMonthIndex = _l[1];
  var maxMonthIndex = useMemo(function () {
    return differenceInMonths(parseISO(maxDate.toString()), parseISO(minDate.toString())) || DEFAULT_MAX_MONTH_INDEX;
  }, [minDate, maxDate]);
  var months = useMemo(function () {
    return buildMonths(minDate, maxDate, locale);
  }, [minDate, maxDate, locale]);
  var firstDayOfMonth = new Date(months[0].year, months[0].monthIndex, 1);
  var firstValidMonth = format(firstDayOfMonth, 'yyyyMM');
  var startMonth = parseISO(firstValidMonth);
  var context = __assign(__assign(__assign({}, props), calendarFormResponse), {
    boxShadow: boxShadow,
    dayWithRoundedCorners: dayWithRoundedCorners,
    displayYear: displayYear,
    firstValidMonth: firstValidMonth,
    locale: locale,
    maxMonthIndex: maxMonthIndex,
    months: months,
    monthIndex: monthIndex,
    monthsLength: months.length,
    monthsToShow: monthsToShow,
    monthWidth: monthWidth,
    scrollToMonth: scrollToMonth,
    startMonth: startMonth,
    useDefaultFooter: useDefaultFooter,
    handleKeyDismiss: handleKeyDismiss,
    onDismiss: onDismiss,
    onDisplayMonthsChange: onDisplayMonthsChange,
    setMonthIndex: setMonthIndex
  });
  return React.createElement(CalendarContext.Provider, {
    value: context
  }, children);
}
export default CalendarProvider;